import FileDownloadLineIcon from 'remixicon-react/FileDownloadLineIcon';
import LinkIcon from 'remixicon-react/LinkIcon';
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon';
import ArrowRightUpLineIcon from 'remixicon-react/ArrowRightUpLineIcon';
import { StyledButton, ButtonType, ArrowCurl, ArrowCurlUp, ArrowCurlDown } from './Button.style';
import CampaignTypeSelector from '../../content/CampaignTypeSelector/CampaignTypeSelector';
import Text from '../Text/Text';
import { getAssetUrl, getUrlLocale } from 'components/utils';
import { pushAnyClickEvent, pushClickCtaEvent } from '../../tracking/events';
import React, { useState } from 'react';
import { HtmlBlock } from 'components/content/templates';
import Modal, { getModalUrl } from '../Modal/Modal';
import ShareSocials, { SHARE_LOCATION } from '../ShareSocials/ShareSocials';

const NO_LINK_BUTTON_TYPES: ButtonType[] = ['download', 'modal'];

interface ButtonFile {
  formats: string[];
  id: string;
  name: string;
  url: string;
  originalFilename: string;
}

interface ButtonModal {
  modalType: 'campaignTypeSelector' | 'share' | 'htmlCode',
  htmlCode?: string
}

interface Props {
  btnType: ButtonType;
  btnText: string;
  btnLink?: string;
  isDisabled?: boolean;
  isDynamicBtn?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onClickCapture?: React.MouseEventHandler<HTMLElement>; // Allow to override onClickCapture event for Data Tracking
  btnFile?: ButtonFile;
  btnModal?: ButtonModal;
  dataAttributes?: object; // Pass any attribute you want to add to the button e.g. { data-attribute="button", data-value="value" }
}

const iconType = (type: ButtonType) => {
  switch (type) {
    case 'external':
      return <ArrowRightUpLineIcon />;
    case 'download':
      return <FileDownloadLineIcon />;
    case 'link':
      return <LinkIcon />;
    case 'arrow':
      return <ArrowRightLineIcon />;
    case 'secondaryArrow':
      return <ArrowRightLineIcon />;
    case 'tcam':
      return <ArrowCurl />;
    case 'tcamUp':
      return <ArrowCurlUp />;
    case 'tcamDown':
      return <ArrowCurlDown />;
    default:
      return null;
  }
};

const Button: React.FC<Props> = ({
  btnText,
  btnLink,
  btnType = 'secondary',
  isDisabled = false,
  isDynamicBtn = false,
  btnFile = { url: undefined, name: '', id: '', formats: [], originalFilename: '' },
  btnModal,
  onClick,
  onClickCapture,
  dataAttributes
}) => {
  const [modalContent, setModalContent] = useState<React.ReactElement | undefined>(undefined);
  const { country, language } = getUrlLocale() as { country: string, language: string };
  const hasIcon = btnType === 'download' || btnType === 'link' || btnType === 'arrow';
  const downloadURL = btnFile?.name ? `${getAssetUrl('file', country, language)}${btnFile?.id}.${btnFile?.formats[0]}` : undefined;
  const href = NO_LINK_BUTTON_TYPES.includes(btnType) ? downloadURL : btnLink;
  const modalName = btnModal ? `button-modal-${btnModal?.modalType}` : '';

  //Data tracking
  const onClickCaptureEvent = (btnText: string, btnType: ButtonType, btnLink?: string, btnModal?: ButtonModal) => {
    if(!NO_LINK_BUTTON_TYPES.includes(btnType)) {
      pushAnyClickEvent({linkText: btnText, linkUrl: btnLink});
    } else if(btnType === 'modal') {
      //Modal buttons also fire a CTA click to the modal url (same as page url + query parameter)
      const modalUrl = getModalUrl(modalName);
      pushClickCtaEvent({linkText: btnText, linkUrl: modalUrl});
    }

    if(btnType === 'modal') {
      switch(btnModal?.modalType) {
        case 'campaignTypeSelector':
          setModalContent(<CampaignTypeSelector />);
          break;
        case 'share':
          setModalContent(<ShareSocials
            shareTitle={document.title}
            shareDescription={document.title}
            location={SHARE_LOCATION.general}
            shareUrl={window.location.href} 
            />);
          break;
        case 'htmlCode':
          setModalContent(<HtmlBlock htmlCode={btnModal.htmlCode || ''} />);
          break;
      }
    }
  }

  return (
    <>
      <StyledButton
        data-attribute="button"
        onClick={onClick}
        onClickCapture={(e)=> (typeof onClickCapture === 'function')? onClickCapture(e) : onClickCaptureEvent(btnText, btnType, btnLink, btnModal)} // Allow to override onClickCapture event
        href={href}
        type={btnType}
        isDisabled={isDisabled}
        isDynamicBtn={isDynamicBtn}
        {...(btnType === 'external' ? { target: '_blank' } : '')}
        {...(btnType === 'download' ? { download: btnFile?.originalFilename } : '')} 
        {...dataAttributes}     
      >
        <Text marginRight={hasIcon ? '0.5rem' : '0'} size={hasIcon ? '1rem' : undefined}>
          {btnText} 
        </Text>
        {iconType(btnType)} 
      </StyledButton>
      {modalContent && <Modal name={modalName} onClose={() => setModalContent(undefined)}>
        {modalContent}
      </Modal>}
    </>
  );
};

export default Button;